import React, { memo, useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import subDays from 'date-fns/subDays';
import { B50 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import type { CreateIssueReturn } from '@atlassian/jira-business-issue-create/src/types.tsx';
import { StatusCategoryIds } from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { DateOnly } from '@atlassian/jira-platform-utils-date-only/src/index.tsx';
import { useCacheActions } from '../../../controllers/cache-actions/index.tsx';
import type { UseCalendarItemsRequest } from '../../../services/calendar-view-items/types.tsx';
import type { JWMCalendarInlineCreateIssue_createJiraBusinessIssue_item as Issue } from '../../../services/create-issue/__generated_apollo__/JWMCalendarInlineCreateIssue.ts';
import type { SetOptimisticItem } from '../../../types.tsx';
import IssueCreatePopover from './issue-create-popover/index.tsx';

type Props = {
	withInlineCreate: boolean;
	startStr: string;
	endStr: string;
	request: UseCalendarItemsRequest;
	setOptimisticItem: SetOptimisticItem;
	onDismiss: () => void;
	onCreatingStart: () => void;
	onCreatingStop: () => void;
};

export const CalendarPlaceholder = ({
	withInlineCreate,
	startStr,
	endStr,
	setOptimisticItem,
	onDismiss,
	request,
	onCreatingStart,
	onCreatingStop,
}: Props) => {
	const [isInlineCreateOpen, setIsInlineCreateOpen] = useState<boolean>(withInlineCreate);
	const { addToCache } = useCacheActions();

	const onPrepareIssueContext = useCallback(
		() => ({
			startDate: new Date(startStr),
			// end date is exclusive in full calendar, hence subtracting one day
			dueDate: subDays(new Date(endStr), 1),
		}),
		[startStr, endStr],
	);

	const onSubmit = useCallback(
		async (payload: CreateIssueReturn<Issue>) => {
			const optimisticItem = {
				summary: payload.optimisticIssuePayload.summary,
				issueTypeId: payload.optimisticIssuePayload.issueType.id,
				key: '',
				id: payload.optimisticIssuePayload.issueId,
				statusCategoryId: StatusCategoryIds.new,
				assignee: null,
				duedate: DateOnly.fromDateAccordingToUTC(
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					new Date(payload.optimisticIssuePayload.dueDate!),
				),
				startdate: DateOnly.fromDateAccordingToUTC(
					// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
					new Date(payload.optimisticIssuePayload.startDate!),
				),
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				iconUrl: payload.optimisticIssuePayload.issueType.avatarUrl!,
			};

			setOptimisticItem(optimisticItem);
			setIsInlineCreateOpen(false);
			onCreatingStart();

			try {
				const newCalendarIssue = await payload.promise;
				if (newCalendarIssue) {
					addToCache(request, newCalendarIssue);
				}
			} catch (e) {
				// swallow error
			} finally {
				setOptimisticItem(null);
				onCreatingStop();
			}
		},
		[setOptimisticItem, onCreatingStart, addToCache, request, onCreatingStop],
	);

	return (
		<PlaceholderContainer data-testid="business-calendar.ui.calendar-view.calendar-placeholder">
			{isInlineCreateOpen && (
				<IssueCreatePopover
					onClose={onDismiss}
					onPrepareIssueContext={onPrepareIssueContext}
					onSubmit={onSubmit}
					renderTrigger={(triggerProps) => (
						// @ts-expect-error TS2698: Spread types may only be created from object types.
						<div {...triggerProps}>
							<Trigger />
						</div>
					)}
				/>
			)}
		</PlaceholderContainer>
	);
};

export default memo<Props>(CalendarPlaceholder);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Trigger = styled.div({
	flex: 1,
	minHeight: '28px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PlaceholderContainer = styled.div({
	border: 0,
	width: '100%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	cursor: 'default',
	minHeight: '28px',
	borderRadius: '4px',
	backgroundColor: token('color.background.selected', B50),
});
